import dialux from '../assets/portfolio/dialux.webp';
import truzeau from '../assets/portfolio/truzeau.webp';
import probuddy from '../assets/portfolio/probuddy.webp';
import trioplus from '../assets/portfolio/trioplus.webp';
import thecarelist from '../assets/portfolio/thecarelist.webp';

/**
 * Menu-sized copies at 640px.
 *
 * The mega menu renders these at ~292 CSS px, so the 1600px covers were
 * being downscaled ~3.4x — the same thing that made the hero look soft.
 * A 640px source is roughly a 1.1x downscale at DPR 2.
 */
import dialuxMenu from '../assets/portfolio/menu/dialux.webp';
import truzeauMenu from '../assets/portfolio/menu/truzeau.webp';
import probuddyMenu from '../assets/portfolio/menu/probuddy.webp';
import trioplusMenu from '../assets/portfolio/menu/trioplus.webp';
import thecarelistMenu from '../assets/portfolio/menu/thecarelist.webp';

/**
 * Portfolio / case studies.
 *
 * Shape
 * -----
 * id          stable key
 * name        client name
 * sector      short category, shown as a pill
 * title       what the product is
 * description one or two sentences of outcome-focused copy
 * image       cover shot, full size (portfolio cards, hero)
 * menuImage    640px copy for the mega menu and any small thumbnail
 * shortName    compact label for dense lists
 * tagline      one short line for the mega menu, where `description` is far
 *              too long for a ~210px card
 * link        destination; anchors back to the section until case-study
 *             pages exist
 * stack       technologies, rendered as chips
 * metrics     up to three post-launch figures — the strongest credibility
 *             device on the page
 *
 * IMAGE FRAMING (all optional)
 * ---------------------------
 * The sources are ~3241x2871 (nearly square) and the card frame is 4:3, so
 * `cover` crops about 15% off the height — ~7.7% from the top and bottom.
 * Adjust per project without touching CSS:
 *
 * imagePosition  which part survives the crop. Any `object-position` value:
 *                'center' (default), 'top', 'bottom', '50% 30%', '40% 20%'.
 *                Lower the second number to keep more of the top.
 * imageFit       'cover' (default, fills and crops) or 'contain' (shows the
 *                whole image, letterboxed onto the card's gradient).
 * imageScale     a multiplier to zoom, e.g. 1.08 to crop in, 0.92 to pull
 *                back. Applies on top of the hover zoom.
 *
 * heroFraming    `{ position, scale }`, used only when a project appears in
 *                the hero stage. Those frames are a different shape from the
 *                portfolio cards and sit on a dark ground, so a shot often
 *                needs a tighter crop there — enough to push the render's
 *                flat studio backdrop outside the frame. Omit to take the
 *                slot defaults in SectionHero.module.css.
 *
 */

const metric = (value, label) => ({ value, label });

export const configPortfolio = [
  {
    id: 'dialux',
    imagePosition: 'center',
    name: 'Dialux Services',
    sector: 'Marketplace',
    title: 'Marketplace for lighting design experts',
    description:
      'A two-sided marketplace connecting specifiers with certified lighting professionals worldwide — verified profiles, project briefs and direct contracting, with payouts handled end to end.',
    image: dialux,
    menuImage: dialuxMenu,
    shortName: 'Dialux',
    tagline: 'The marketplace for certified lighting design professionals.',
    link: '#portfolio',
    featured: true,
    stack: ['React', 'Node.js', 'Stripe Connect'],
    metrics: [
      metric('401', 'Verified pros'),
      metric('50', 'Countries'),
      metric('4.2×', 'Briefs matched / mo'),
    ],
  },
  {
    id: 'probuddy',
    imagePosition: 'center',
    name: 'Probuddy',
    sector: 'Marketplace',
    title: 'Booking platform for sports, wellness and tutoring',
    description:
      'A booking platform where customers find verified sports, wellness and tutoring experts, see real-time availability and pay in one flow.',
    image: probuddy,
    menuImage: probuddyMenu,
    shortName: 'Probuddy',
    tagline: 'Booking for sports, wellness and tutoring experts.',
    link: '#portfolio',
    stack: ['React Native', 'Node.js', 'MongoDB', 'Stripe'],
    metrics: [
      metric('6k+', 'Bookings'),
      metric('3', 'Marketplaces in one app'),
      metric('99.9%', 'Uptime'),
    ],
  },
  {
    id: 'trioplus',
    imagePosition: 'center',
    name: 'TRIO Plus',
    sector: 'PropTech',
    title: 'Housing platform for educators',
    description:
      'Affordable housing discovery for teachers, with mapped inventory, eligibility screening and landlord onboarding.',
    image: trioplus,
    menuImage: trioplusMenu,
    shortName: 'TRIO Plus',
    tagline: 'Affordable housing discovery for educators.',
    link: '#portfolio',
    stack: ['React', 'Express', 'PostgreSQL', 'Mapbox'],
    metrics: [
      metric('3k', 'Listings indexed'),
      metric('−38%', 'Time to placement'),
      metric('6 wks', 'Brief to release'),
    ],
  },
  {
    id: 'truzeau',
    imagePosition: 'center 20%',
    name: 'Truzeau',
    sector: 'Commerce',
    title: 'New & preloved fashion marketplace',
    description:
      'Seller onboarding, condition grading and an editorial storefront tuned for mobile-first browsing.',
    image: truzeau,
    menuImage: truzeauMenu,
    shortName: 'Truzeau',
    tagline: 'New and preloved South Asian fashion resale.',
    link: '#portfolio',
    stack: ['React', 'Node.js', 'Stripe', 'AWS'],
    metrics: [
      metric('2.1×', 'Listing conversion'),
      metric('−45%', 'Checkout drop-off'),
      metric('4.8', 'Store rating'),
    ],
  },
  {
    id: 'thecarelist',
    imagePosition: 'center',
    name: 'The Care List',
    sector: 'Marketplace',
    title: 'Childcare discovery and booking',
    description:
      'Helps families find and book the right daycare, with licence verification, availability and enquiry management for providers.',
    image: thecarelist,
    menuImage: thecarelistMenu,
    shortName: 'The Care List',
    tagline: 'Find and book the right daycare or day home.',
    link: '#portfolio',
    stack: ['React', 'Node.js', 'MongoDB', 'Twilio'],
    metrics: [
      metric('1.4k', 'Providers onboarded'),
      metric('72%', 'Enquiry response'),
      metric('3 mo', 'MVP to revenue'),
    ],
  },
];
